import { FormattedMessage } from "react-intl";
import { SaviyntLogo } from "../SaviyntLogo";

interface BrandLockupProps {
  /**
   * Surface treatment. "dark" = light text on a dark surface (e.g. the
   * always-dark sidenav or the header in dark mode); "light" = dark text on a
   * light surface (the header in light mode). Also selects the logo wordmark.
   */
  variant: "light" | "dark";
  /** "sm" for the narrow sidenav, "md" for the roomy top header. */
  size?: "sm" | "md";
}

const SIZE = {
  sm: { logo: "h-5", divider: "h-[16px]", text: "text-[15px]", gap: "gap-2" },
  md: { logo: "h-7", divider: "h-[20px]", text: "text-[18px]", gap: "gap-3" },
} as const;

const VARIANT = {
  dark: { divider: "bg-white/20", text: "text-slate-300" },
  light: { divider: "bg-slate-300", text: "text-slate-900" },
} as const;

/**
 * Brand lockup: the Saviynt wordmark, a thin divider, and the "Zuma" product
 * label. Used in the expanded sidenav header and — when the sidenav is
 * collapsed/unpinned — in the top header bar.
 */
export function BrandLockup({ variant, size = "sm" }: BrandLockupProps) {
  const s = SIZE[size];
  const v = VARIANT[variant];

  return (
    <span className={`flex min-w-0 items-center ${s.gap}`}>
      <SaviyntLogo variant={variant} className={`${s.logo} shrink-0`} />
      <span
        className={`w-px shrink-0 ${s.divider} ${v.divider}`}
        aria-hidden="true"
      />
      <span className={`truncate font-normal leading-none ${s.text} ${v.text}`}>
        <FormattedMessage
          id="mYKkZ/"
          defaultMessage="Zuma"
          description="Product name shown beside the Saviynt logo in the sidenav"
        />
      </span>
    </span>
  );
}
